import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  ZONE, findSkinImage, fmt, pct, signed, rise,
  ZoneHead, ZoneStats, ZoneSearch, ZoneChips, ZoneEmpty, ZoneFooter,
} from './zoneShared';
import { archiveName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal from './ItemDetailModal';
import WishlistAddModal from './WishlistAddModal';

// Wishlist Zone — the Watchboard (Wishlist 2) on the Overview 7 board.
// Each wish is a card with a progress-to-target meter and a TARGET HIT sticker.
const CONDITION_MAP = {
  'Factory New':   'Factory New',
  'Minimal Wear':  'Minimal Wear',
  'Field Tested':  'Field-Tested',
  'Well Worn':     'Well-Worn',
  'Battle Scarred':'Battle-Scarred',
};

function getWishPrice(livePrices, item) {
  if (!livePrices || !item.skin) return null;
  if (item.condition) {
    const cond = CONDITION_MAP[item.condition] || item.condition;
    return (livePrices[`${item.skin} (${cond})`] || livePrices[item.skin])?.starting_at?.price ?? null;
  }
  return livePrices[item.skin]?.starting_at?.price ?? null;
}

function Spark({ history }) {
  const prices = (history || []).map(d => d.price).filter(p => p != null);
  if (prices.length < 2) {
    return <span style={{ fontSize: 14, fontWeight: 800, color: ZONE.mute, textTransform: 'uppercase' }}>collecting…</span>;
  }
  const min = Math.min(...prices), max = Math.max(...prices), span = max - min || 1;
  const w = 112, h = 28;
  const pts = prices.map((p, i) =>
    `${(i / (prices.length - 1) * w).toFixed(1)},${(h - 3 - (p - min) / span * (h - 6)).toFixed(1)}`).join(' ');
  // Falling price is good news when you're waiting to buy.
  const down = prices[prices.length - 1] <= prices[0];
  return (
    <svg width={w} height={h} style={{ display: 'block', overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke={ZONE.black} strokeWidth="3"
        strokeLinejoin="round" strokeLinecap="round" />
      <polyline points={pts} fill="none" stroke={down ? ZONE.green : ZONE.hot} strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const FILTERS = [
  { key: 'all',     label: 'All' },
  { key: 'hit',     label: 'Target hit' },
  { key: 'waiting', label: 'Waiting' },
];

export default function WishlistTabZone({
  wishlist, livePrices, csfloatPrices, skinsData = [],
  itemsWithConditions, onSave, onDelete,
}) {
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);   // the wish whose modal is open
  // { initial } while the add/edit modal is open; initial null means adding.
  const [editor, setEditor] = useState(null);

  const items = useMemo(() => {
    return (wishlist || []).map((w, wi) => {
      const price = getWishPrice(livePrices, w);
      const hit   = price != null && w.targetPrice != null && price <= w.targetPrice;
      // Progress toward target: how far price has fallen from its watched high
      const hist  = (w.priceHistory || []).map(d => d.price).filter(p => p != null);
      const high  = hist.length ? Math.max(...hist, price ?? 0) : price;
      let progress = null;
      if (price != null && w.targetPrice != null && high != null && high > w.targetPrice) {
        progress = Math.max(0, Math.min(1, (high - price) / (high - w.targetPrice)));
      } else if (hit) progress = 1;
      const gapPct = price != null && w.targetPrice ? (price - w.targetPrice) / w.targetPrice * 100 : null;
      // Carried on the item so the pricing pass and the render pass agree
      // across the sort and the filter that sit between them.
      return { ...w, price, hit, progress, gapPct, _k: w.id || `wish${wi}` };
    }).sort((a, b) => (b.hit - a.hit) || ((a.gapPct ?? Infinity) - (b.gapPct ?? Infinity)));
  }, [wishlist, livePrices]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(w => {
      if (filter === 'hit' && !w.hit) return false;
      if (filter === 'waiting' && w.hit) return false;
      if (q && !w.skin.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, query, filter]);

  const hits = items.filter(w => w.hit).length;

  // What each wish has done since it was written down. The sparkline only
  // covers days this app was running; the archive covers the whole wait.
  const archivePoints = useMemo(() => items.map(w => ({
    key: w._k, name: archiveName(w.skin, w.condition), date: dayOf(w.createdAt),
  })), [items]);
  const archive = useSinceMoves(archivePoints, csfloatPrices);

  const totals = useMemo(() => {
    const withTarget = items.filter(w => w.targetPrice != null);
    const budget = withTarget.reduce((s, w) => s + w.targetPrice, 0);
    const now    = items.reduce((s, w) => s + (w.price ?? 0), 0);
    const gaps   = withTarget.map(w => w.gapPct).filter(g => g != null);

    const drift = items.reduce((acc, w) => {
      const mv = archive.get(w._k);
      if (!mv) return acc;
      return {
        change: acc.change + mv.change, base: acc.base + mv.then,
        n: acc.n + 1, cheaper: acc.cheaper + (mv.change < 0 ? 1 : 0),
      };
    }, { change: 0, base: 0, n: 0, cheaper: 0 });

    return {
      budget, now, drift,
      driftPct: drift.base > 0 ? (drift.change / drift.base) * 100 : null,
      avgGap: gaps.length ? gaps.reduce((s, g) => s + g, 0) / gaps.length : null,
    };
  }, [items, archive]);

  // Adding and editing are the same modal, so both entry points open it the
  // same way: the empty state below and the button on the toolbar.
  const modal = editor && onSave && (
    <WishlistAddModal
      variant="zone"
      initial={editor.initial}
      wishlist={wishlist || []}
      skinsData={skinsData}
      itemsWithConditions={itemsWithConditions}
      livePrices={livePrices}
      onSave={onSave}
      onDelete={onDelete}
      onClose={() => setEditor(null)}
    />
  );

  if (!items.length) {
    return (
      <div className="zn-root">
        <ZoneHead pre="THE" hl="WATCH" post="BOARD" sub="Skins you're stalking, and how close they are to your price." />
        <ZoneEmpty>
          {onSave ? (
            <>
              The watchboard is empty. Put a skin on it and its price gets tracked from
              today, owned or not.
              <div style={{ marginTop: 16 }}>
                <button className="zn-btn primary" onClick={() => setEditor({ initial: null })}>
                  Add your first wish
                </button>
              </div>
            </>
          ) : 'The watchboard is empty. Heart an item anywhere in the app to start watching it.'}
        </ZoneEmpty>
        {modal}
      </div>
    );
  }

  return (
    <div className="zn-root">

      <ZoneHead
        pre="THE" hl="WATCH" post="BOARD"
        sub="Every skin you're waiting on. The meter fills as the price drops toward your target."
        stickers={[
          { text: `${items.length} on the board`, bg: ZONE.teal },
          hits > 0
            ? { text: `${hits} at target ✷`, bg: ZONE.lime }
            : { text: 'nothing at target yet', bg: ZONE.white },
        ]}
      />

      <ZoneStats items={[
        { label: 'Ready to buy', value: String(hits), sub: `of ${items.length} watched skins`, bg: hits > 0 ? ZONE.lime : ZONE.white },
        { label: 'Target budget', value: `$${fmt(totals.budget)}`, sub: 'cost if every target filled today', bg: ZONE.yellow },
        { label: 'Asking now', value: `$${fmt(totals.now)}`,
          sub: totals.avgGap != null ? `avg ${pct(totals.avgGap)} above target` : 'no live prices yet',
          bg: ZONE.purple },
        // Needs the price archive; hidden when nothing could be priced.
        totals.drift.n > 0 && {
          label: 'Since you started watching', value: signed(totals.drift.change),
          sub: `${totals.drift.cheaper} of ${totals.drift.n} priced are cheaper than when you added them`,
          // A watchlist that fell is a win for the buyer, so the polarity is
          // the reverse of every other board here.
          bg: totals.drift.change <= 0 ? ZONE.lime : ZONE.pink },
      ].filter(Boolean)} />

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="zn-bar">
        <ZoneSearch value={query} onChange={setQuery} placeholder="Search watchboard…" />
        <ZoneChips
          options={FILTERS.map(f => ({ ...f, label: f.key === 'hit' && hits > 0 ? `${f.label} (${hits})` : f.label }))}
          value={filter} onChange={setFilter}
        />
        <span className="zn-bar-actions" style={{ marginLeft: 'auto' }}>
          <span className="zn-count">{filtered.length} shown</span>
          {onSave && (
            <button className="zn-btn primary" onClick={() => setEditor({ initial: null })}
              title="Watch any skin, whether you own it or not">
              + Add item
            </button>
          )}
        </span>
      </div>

      {/* ── Cards ──────────────────────────────────── */}
      {filtered.length === 0 ? (
        <ZoneEmpty>Nothing matches your filters.</ZoneEmpty>
      ) : (
        <div className="zn-wish-grid">
          {filtered.map((w, i) => {
            const img = findSkinImage(skinsData, w.skin, w.condition);
            return (
              <motion.div key={w._k} className={`zn-wish click ${w.hit ? 'hit' : ''}`} {...rise(i)}
                role="button" tabIndex={0}
                title="Open details"
                onClick={() => setDetail({ ...w, img })}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail({ ...w, img }); } }}>

                {w.hit && <span className="zn-wish-badge">TARGET HIT</span>}

                <div className="zn-wish-row">
                  {img
                    ? <img src={img} alt="" />
                    : <div className="zn-thumb-blank" style={{ width: 78, height: 52 }} />}
                  <div style={{ minWidth: 0 }}>
                    <div className="zn-wish-name" title={w.skin}>{w.skin}</div>
                    <div className="zn-wish-cond">
                      {w.condition || 'Any condition'} · watching since{' '}
                      {w.createdAt ? new Date(w.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'n/a'}
                    </div>
                  </div>
                </div>

                <div className="zn-wish-prices">
                  <span className="cur">{w.price != null ? `$${fmt(w.price)}` : 'n/a'}</span>
                  <span className="tgt">{w.targetPrice != null ? `target $${fmt(w.targetPrice)}` : 'no target set'}</span>
                </div>

                {w.progress != null && (
                  <>
                    <div className="zn-meter">
                      <motion.div className="fill"
                        style={{ background: w.hit ? ZONE.green : ZONE.yellow }}
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.round(w.progress * 100)}%` }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 + Math.min(i, 16) * 0.04 }}
                      />
                    </div>
                    <div className="zn-wish-gap" style={{ color: w.hit ? ZONE.win : ZONE.mute }}>
                      {w.hit
                        ? `${Math.abs(w.gapPct).toFixed(1)}% under target — time to buy?`
                        : w.gapPct != null ? `${w.gapPct.toFixed(1)}% above target` : ''}
                    </div>
                  </>
                )}

                {(() => {
                  const mv = archive.get(w._k);
                  if (!mv) return null;
                  return (
                    <div className="zn-wish-gap" title={readingNote(mv)}
                      style={{ color: mv.change <= 0 ? ZONE.win : ZONE.mute }}>
                      {mv.change <= 0 ? 'down' : 'up'} {Math.abs(mv.pct).toFixed(1)}% since you added it · was ${fmt(mv.then)}
                    </div>
                  );
                })()}

                <div className="zn-wish-spark">
                  <span className="lbl">Price trail</span>
                  <Spark history={w.priceHistory} />
                  {/* In the footer rather than the corner, which the TARGET HIT
                      sticker owns. Stops the click reaching the card, whose own
                      job is opening the detail modal. */}
                  {onSave && (
                    <button className="zn-wish-edit" title="Edit target, note or wear"
                      onClick={e => { e.stopPropagation(); setEditor({ initial: w }); }}>
                      Edit
                    </button>
                  )}
                </div>

                {w.note && <div className="zn-wish-note" title={w.note}>“{w.note}”</div>}
              </motion.div>
            );
          })}
        </div>
      )}

      <ZoneFooter stickers={[
        { text: 'prices via buff163', bg: ZONE.purple },
        { text: 'green trail = price falling', bg: ZONE.white },
        { text: `${hits} ready ✦ ${items.length - hits} waiting`, bg: ZONE.orange, right: true },
      ]} />

      {detail && (
        <ItemDetailModal
          variant="zone"
          kind="wish"
          name={detail.skin}
          condition={detail.condition}
          image={detail.img}
          history={detail.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          targetPrice={detail.targetPrice}
          note={detail.note}
          createdAt={detail.createdAt}
          onClose={() => setDetail(null)}
        />
      )}

      {modal}
    </div>
  );
}
